import React, { useState } from 'react';
import { useOasisBackend } from '../../../hooks/useOasisBackend';
import SubmitButton from '../SubmitButton';

interface CreateVectorOntologyProps {
  datasetId: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CreateVectorOntology: React.FC<CreateVectorOntologyProps> = ({ datasetId, onSuccess, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [vectorOntology, setVectorOntology] = useState<string[]>([]);
  const [useKeys, setUseKeys] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const db = useOasisBackend();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await db.endpoints.testsuite.testsuiteApiTestsuiteCreateVectorOntologyCreate({
        vector_ontology: vectorOntology,
        dataset_id: datasetId,
        use_keys: useKeys,
        name,
        description
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating vector ontology:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddVector = () => {
    const newVectorOntology = [...vectorOntology, ''];
    setVectorOntology(newVectorOntology);
    // Update bulk input to match individual fields
    setBulkInput(newVectorOntology.join(', '));
  };

  const handleRemoveVector = (index: number) => {
    const newVectorOntology = vectorOntology.filter((_, i) => i !== index);
    setVectorOntology(newVectorOntology);
    // Update bulk input to match individual fields
    setBulkInput(newVectorOntology.join(', '));
  };

  const handleVectorChange = (index: number, value: string) => {
    const newVectorOntology = [...vectorOntology];
    newVectorOntology[index] = value;
    setVectorOntology(newVectorOntology);
    // Update bulk input to match individual fields
    setBulkInput(newVectorOntology.join(', '));
  };

  const handleAddKey = () => {
    setUseKeys([...useKeys, '']);
  };

  const handleRemoveKey = (index: number) => {
    setUseKeys(useKeys.filter((_, i) => i !== index));
  };

  const handleKeyChange = (index: number, value: string) => {
    const newUseKeys = [...useKeys];
    newUseKeys[index] = value;
    setUseKeys(newUseKeys);
  };

  const handleBulkInput = (value: string) => {
    setBulkInput(value);
    // Split by comma and clean up each dimension
    const dimensions = value
      .split(',')
      .map(dim => dim.trim().replace(/^["']|["']$/g, '')) // Remove quotes
      .filter(dim => dim.length > 0); // Remove empty strings
    setVectorOntology(dimensions);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
          Name
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
          Description
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="bulkInput">
          Bulk Input Dimensions (comma-separated)
        </label>
        <textarea
          id="bulkInput"
          value={bulkInput}
          onChange={(e) => handleBulkInput(e.target.value)}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          placeholder="Enter dimensions separated by commas (e.g., dimension1, dimension2, dimension3)"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Vector Ontology (Individual Fields)
        </label>
        <div className="space-y-2">
          {vectorOntology.map((vector, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={vector}
                onChange={(e) => handleVectorChange(index, e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter vector component"
                required
              />
              <button
                type="button"
                onClick={() => handleRemoveVector(index)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddVector}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Add Vector Component
          </button>
        </div>
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Use Keys (Optional)
        </label>
        <div className="space-y-2">
          {useKeys.map((key, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={key}
                onChange={(e) => handleKeyChange(index, e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter key"
              />
              <button
                type="button"
                onClick={() => handleRemoveKey(index)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddKey}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Add Key
          </button>
        </div>
      </div>

      <div className="flex justify-end gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Cancel  
          </button>
        )}
        <SubmitButton
          isSubmitting={isSubmitting}
          submitText="Create Vector Ontology"
          loadingText="Creating..."
        />
      </div>
    </form>
  );
};

export default CreateVectorOntology; 